import { useEffect, useState } from "react";
import { api, formatApiError, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Upload, FolderOpen, ExternalLink, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["bylaws", "agreements", "bills", "minutes", "amc", "photos", "other"];

export default function DocumentsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [community, setCommunity] = useState(null);
  const [filter, setFilter] = useState("all");

  async function load() {
    const r = await api.get("/documents");
    setItems(r.data);
  }
  useEffect(() => {
    load();
    api.get("/communities/mine").then((r) => setCommunity(r.data));
  }, []);

  async function del(d) {
    if (!confirm(`Remove "${d.title}"?`)) return;
    try { await api.delete(`/documents/${d.id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Documents</div>
          <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">Apartment files</h1>
        </div>
        <UploadDialog onDone={load} />
      </div>

      {community?.drive_link && (
        <a href={community.drive_link} target="_blank" rel="noreferrer"
          className="mt-5 block bg-[#FDFBF7] border border-[#E8D8D3] rounded-2xl p-4 hover:border-[#C05640] transition" data-testid="drive-link">
          <div className="flex items-center gap-3">
            <FolderOpen size={24} className="text-[#C05640]" />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-[#C05640] font-bold">Shared drive</div>
              <div className="text-sm text-stone-800 truncate">{community.drive_link}</div>
              <div className="text-xs text-stone-500 mt-0.5">Bigger files live in the apartment's Google Drive / OneDrive.</div>
            </div>
            <ExternalLink size={16} className="text-stone-400" />
          </div>
        </a>
      )}

      <div className="mt-5 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
        {["all", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`doc-cat-${c}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs capitalize border ${
              filter === c ? "bg-[#C05640] text-white border-[#C05640]" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
            }`}>{c}</button>
        ))}
      </div>

      <div className="mt-5 space-y-2.5">
        {filtered.map((d) => {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${d.file.storage_path}`;
          return (
            <div key={d.id} className="bg-white border border-stone-200 rounded-xl p-3 flex items-center gap-3" data-testid={`doc-${d.id}`}>
              <FileText size={20} className="text-stone-400 shrink-0" />
              <a href={url} target="_blank" rel="noreferrer" className="flex-1 min-w-0 hover:underline">
                <div className="font-medium text-stone-900 truncate">{d.title}</div>
                <div className="text-xs text-stone-500">{d.category} · {fmtDate(d.created_at)} · by {d.uploaded_by_name}</div>
              </a>
              {(user?.role === "committee" || d.uploaded_by === user?.id) && (
                <button onClick={() => del(d)} className="text-stone-400 hover:text-red-600 p-2" data-testid={`doc-del-${d.id}`}><Trash2 size={14} /></button>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-stone-500 italic py-10 text-center">No documents in this category.</div>}
      </div>
    </div>
  );
}

function UploadDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(""); const [category, setCategory] = useState("other");
  const [file, setFile] = useState(null); const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!file) return toast.error("Pick a file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd);
      await api.post("/documents", { title: title || file.name, category, file: up.data, tags: [] });
      toast.success("Document added");
      setTitle(""); setCategory("other"); setFile(null); setOpen(false);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="doc-upload-btn" className="rounded-full bg-[#C05640] hover:bg-[#A54734]">
          <Upload size={16} className="mr-1.5" /> Upload
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">Upload a document</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div><Label>File</Label><Input type="file" required onChange={(e) => setFile(e.target.files?.[0])} data-testid="doc-file" className="h-11 mt-1.5" /></div>
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title" className="h-11 mt-1.5" placeholder="Society bylaws 2024" /></div>
          <div>
            <Label>Category</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-cat"
              className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white capitalize">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <p className="text-xs text-stone-500">Storage on NivasOne is small for now — for bigger archives, set a shared Google Drive link in Settings.</p>
          <Button type="submit" disabled={busy} data-testid="doc-save"
            className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Uploading…" : "Upload"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
